import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontWeight, LiliButton, Text } from '@lili/lili-frontend-common';
import { useStore } from '../../../../store';
import { AUTO_SAVING_TEXTS, BUSINESS_TYPE, INPUT_MONEY_CSS, TAX_BUCKET_PERSONALIZATION } from '../../constants';
import Icon from '../../../../generic-components/Icon';
import areYouFreelancer from '../../../../assets/icons/svg/large-icons/ic-large-freelancer.svg';
import percentageLabel from '../../../../assets/icons/svg/colored-icons/ic-colored-bubble.svg';
import estimatedTaxRateIcon from '../../../../assets/svg/estimatedTaxRateIcon.svg';
import { useTaxBucketInfoQuery } from '../../../../hooks/query/useTaxBucketInfoQuery';
import Donut from '../../../../generic-components/Donut';
import { useTaxBucketSetPercentAndActivate } from '../../../../hooks/query/useTaxBucketSetPercentAndActivate';
import { currencyInputValueToNumber, handleInputWithCurrencyOnChange } from '../../../../helpers/currency.helper';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import InputWithCurrency from '../../../../generic-components/InputWithCurrency';
import { CURRENCY_USD_SIGN, ENTER_KEY } from '../../../../constants/general';
import { BUSINESS_NAME_APPROVED } from '../../../profile/constants';
import { useUploadGenericFile } from '../../../../hooks/utilities/useUploadGenericFile';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../../types';
// import { LiliCurrencyInputExpandable } from '@lili/lili-frontend-common';

interface IStateObject {
  currentStep: number;
  hasUserOtherSources: boolean | null;
  isBusinessValid: boolean;
  annualIncome: number;
  annualExpense: number;
  savePercent: number;
  annualSalary: number;
  currentStepNumber: number;
  estimatedTaxRate?: number;
}

interface IProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

const TaxBucketPersonalization = ({ currentStep, setCurrentStep }: IProps) => {
  const navigate = useNavigate();
  const { refetch: updateSummary } = useAccountSummaryQuery(() => {});
  const navigateWIthParams = (
    isSavingsActivated: string,
    isSuccess: boolean,
    activation: boolean,
    savePercent: number,
  ) => {
    navigate(`/tax-bucket-dashboard/:${isSavingsActivated}-${isSuccess}-${activation}-${savePercent}`);
  };
  const summary = useStore((state) => state.summary);
  const businessName = summary?.business?.name;
  const {
    HEADER,
    TEXT,
    STEPS,
    BUTTON_TITLES,
    INITIAL_STATE_OBJECT,
    INPUT_STEP_IN_PX,
    INITIAL_PERCENTAGE,
    INITIAL_MARGIN_ESTIMATED_PERCENTAGE,
    MAX_RECOMMENDED_PERCENT,
    MIN_RECOMMENDED_PERCENT,
    DEFAULT_ESTIMATED_PERCENT,
    RECOMMENDATION_STEP_IN_PX,
  } = TAX_BUCKET_PERSONALIZATION;
  const { DISCLAIMER } = AUTO_SAVING_TEXTS;
  const { STEP_1, STEP_2, STEP_2_OTHER, STEP_3, STEP_4, STEP_CONFIRMATION } = STEPS;
  const INITIAL_INPUT_VALUE = '';
  const [userState, setUserState] = useState<IStateObject>({ ...INITIAL_STATE_OBJECT });
  const [inputValue, setInputValue] = useState<string>(INITIAL_INPUT_VALUE);
  const [percentage, setPercentage] = useState<number>(INITIAL_PERCENTAGE);

  const { customerId } = useStore((state) => state.user);
  const { mutateAsync: postUploadSnapShot, isLoading: isLoadingUploadFile } = useUploadGenericFile();
  const checkBusinessValidation = () => {
    const businessObject = summary.business;
    if (businessObject && businessObject.status && businessObject.type) {
      const { LLC, DBA } = BUSINESS_TYPE;
      const isStatusValid = businessObject.status === BUSINESS_NAME_APPROVED;
      const isBusinessTypeValid = businessObject.type !== LLC && businessObject.type !== DBA;
      if (isStatusValid && isBusinessTypeValid) {
        setUserState({ ...userState, isBusinessValid: true });
        return;
      }
    }
    if (userState.isBusinessValid) setUserState({ ...userState, isBusinessValid: false });
  };
  const goToNextStep = () => {
    setUserState({
      ...userState,
      currentStep: userState.currentStep + 1,
      currentStepNumber: userState.currentStepNumber + 1,
    });
  };

  const {
    data,
    refetch: sendTaxInfo,
    isSuccess,
  } = useTaxBucketInfoQuery({
    customerId,
    fullTimeFreelancer: userState.hasUserOtherSources ?? false,
    annualExpense: userState.annualExpense,
    annualIncome: userState.annualIncome,
    annualSalary: userState.annualSalary,
    onSuccess: () => {
      goToNextStep();
    },
  });

  const { refetch: setupAndActivate, isLoading } = useTaxBucketSetPercentAndActivate({
    customerId,
    savePercent: userState.savePercent,
    activate: true,
    onSuccess: () => {
      const isActivated = summary.taxBucketBalance.active;
      updateSummary().then(() => navigateWIthParams('isSavingsActivated', true, !isActivated, userState.savePercent));
    },
  });

  const handleSubmitPercentValue = async () => {
    await postUploadSnapShot({
      fileUsageType: GENERIC_FILE_UPLOAD_TYPES.TAX_BUCKET_SETUP,
      imageName: `${Date.now()}`,

    });
    await setupAndActivate();
  };

  const handleHasUserOtherSources = (hasUserOtherSources: boolean) => {
    if (hasUserOtherSources) {
      setUserState({
        ...userState,
        hasUserOtherSources,
        currentStep: STEP_2_OTHER,
        currentStepNumber: STEP_2,
      });
    } else {
      setUserState({
        ...userState,
        hasUserOtherSources,
        currentStep: STEP_3,
        currentStepNumber: STEP_2_OTHER,
      });
    }
  };

  const handleAnnualSalary = (annualSalary: number) => {
    if (annualSalary) {
      setUserState({
        ...userState,
        annualSalary,
        currentStep: STEP_3,
        currentStepNumber: STEP_2_OTHER,
      });
    }
  };
  const handleAverageIncome = (annualIncome: number) => {
    if (!userState.isBusinessValid) {
      setUserState({
        ...userState,
        annualIncome,
        currentStep: STEP_2,
        currentStepNumber: STEP_2,
      });
    } else {
      setUserState({
        ...userState,
        annualIncome,
        currentStep: STEP_3,
        currentStepNumber: STEP_2,
      });
    }
  };
  const handleAverageExpenses = (annualExpense: number) => {
    setUserState({
      ...userState,
      annualExpense,
    });
  };
  const handleSetSavePercent = (savePercent: number) => {
    setUserState({
      ...userState,
      savePercent,
      currentStep: STEP_CONFIRMATION,
    });
  };

  const handleOnChange = (event: { target: { value: string } }) => {
    handleInputWithCurrencyOnChange(event, setInputValue);
  };

  const labelMargin = (percentage - INITIAL_PERCENTAGE) * INPUT_STEP_IN_PX;
  const recommendedMargin =
    ((data && (data.estimatedTaxRate || typeof data.estimatedTaxRate === 'number')
      ? (data.estimatedTaxRate > MAX_RECOMMENDED_PERCENT
        ? MAX_RECOMMENDED_PERCENT
        : data.estimatedTaxRate < MIN_RECOMMENDED_PERCENT
          ? MIN_RECOMMENDED_PERCENT
          : data.estimatedTaxRate) * 100
      : DEFAULT_ESTIMATED_PERCENT) -
      INITIAL_PERCENTAGE) *
      RECOMMENDATION_STEP_IN_PX -
    INITIAL_MARGIN_ESTIMATED_PERCENTAGE;
  const getRecommendedPercentage = () => {
    if (data && (data.estimatedTaxRate || typeof data.estimatedTaxRate === 'number')) {
      return data.estimatedTaxRate < MIN_RECOMMENDED_PERCENT
        ? MIN_RECOMMENDED_PERCENT
        : data.estimatedTaxRate > MAX_RECOMMENDED_PERCENT
          ? MAX_RECOMMENDED_PERCENT
          : data.estimatedTaxRate;
    }
    return 0;
  };

  const recommendedPercentage =
    getRecommendedPercentage() !== 0 ? getRecommendedPercentage() * 100 : DEFAULT_ESTIMATED_PERCENT;

  const inputRef = useRef<HTMLInputElement>();

  const isButtonDisabled = inputValue.trim().length < 1;

  useEffect(() => {
    if (userState.currentStep !== STEP_4) {
      setInputValue(INITIAL_INPUT_VALUE);
    }
  }, [userState.currentStep]);

  useEffect(() => {
    if (userState.annualExpense !== INITIAL_STATE_OBJECT.annualExpense) {
      sendTaxInfo();
    }
  }, [userState.annualExpense]);

  useEffect(() => {
    if (isSuccess && data && typeof data.estimatedTaxRate === 'number') {
      setPercentage(getRecommendedPercentage() * 100);
    } else setPercentage(DEFAULT_ESTIMATED_PERCENT);
  }, [data]);

  useEffect(() => {
    if (currentStep !== userState.currentStepNumber) {
      if (userState.currentStep === STEP_4 && userState.isBusinessValid) {
        setUserState({
          ...userState,
          currentStep: STEP_3,
          currentStepNumber: STEP_2,
        });
      } else if (
        userState.currentStep === STEP_2_OTHER &&
        userState.hasUserOtherSources &&
        !userState.isBusinessValid
      ) {
        setUserState({
          ...userState,
          currentStep: STEP_2,
          currentStepNumber: STEP_2,
        });
      } else if (currentStep === STEP_2_OTHER && !userState.hasUserOtherSources && !userState.isBusinessValid) {
        setUserState({
          ...userState,
          currentStep: STEP_1,
          currentStepNumber: userState.currentStepNumber - 1,
        });
      } else {
        setUserState({
          ...userState,
          currentStep,
          currentStepNumber: userState.currentStepNumber - 1,
        });
      }
    }
    if (inputRef.current && (currentStep === STEP_2 || currentStep === STEP_3)) inputRef.current.focus();
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== userState.currentStepNumber) {
      setCurrentStep(userState.currentStepNumber);
    }
    if (inputRef.current && (currentStep === STEP_2 || currentStep === STEP_3)) inputRef.current.focus();
  }, [userState.currentStep]);

  useEffect(() => {
    if (!userState.hasUserOtherSources && userState.annualSalary) {
      setUserState({ ...userState, annualSalary: 0 });
    }
  }, [userState.hasUserOtherSources]);

  useEffect(() => {
    if (inputRef.current && (currentStep === STEP_2 || currentStep === STEP_3)) inputRef.current.focus();
    checkBusinessValidation();
  }, []);

  const { t } = useTranslation();

  return (
    <div
      className='w-full h-[75dvh] bg-surface-primary-bw-00 shadow-widgetCard flex flex-col items-center rounded-[12px] justify-center  border border-outline-primary-bw-03'
    >
      <div className='h-[1.563rem] text-xxs font-metric text-bw05 flex items-center'>
        {userState.currentStep < STEP_4 ? `STEP ${userState.currentStepNumber}/3` : ''}
      </div>
      {userState.currentStep === STEP_1 && (
        <div className='flex flex-col items-center w-[350px] mt-[28px]'>
          <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='text-center'>
            {t(HEADER.STEP_2_1)}
            <span className='text-text-brand-primary'>{t(HEADER.STEP_2_GREEN_WORD)}</span>
            {businessName ? `${t(HEADER.STEP_2_2)} ${businessName}?` : t(HEADER.STEP_2_3)}
          </Text.H8>
          <Text.H10 className='text-center h-[2.125rem] mt-[8px] mb-[40px] whitespace-pre-line text-text-main-secondary'>
            {t(TEXT.STEP_2)}
          </Text.H10>
          <InputWithCurrency
            containerClassName=''
            currency={CURRENCY_USD_SIGN}
            currencyClassName='font-metricMedium text-base text-bw07'
            id='AverageIncomeInput'
            inputCLassName={`h-[4rem] w-[17.5rem] focus:border-action03 caret-action03 ${INPUT_MONEY_CSS} ${inputValue === '' ? 'border-bw08' : 'border-action03'}`}
            onChange={handleOnChange}
            value={inputValue}
            inputRef={inputRef as React.Ref<HTMLInputElement>}
            handleKeyDown={() => {
              if (!isButtonDisabled) handleAverageIncome(currencyInputValueToNumber(inputValue));
            }}
            eventKey={ENTER_KEY}
          />
          <LiliButton
            disabled={isButtonDisabled}
            onClick={() => {
              handleAverageIncome(currencyInputValueToNumber(inputValue));
            }}
            className='w-[17.5rem] h-[3.125rem] mt-[40px]'
            id='setIncomeButton'
            title={t(BUTTON_TITLES.CONTINUE_BTN)}
          />
        </div>
      )}
      {userState.currentStep === STEP_2 && (
      <div className='flex flex-col items-center w-[350px] mt-[28px]'>
        <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='text-center'>
          {t(HEADER.STEP_1)}
        </Text.H8>
        <Text.H10 className='mt-[8px] text-text-main-secondary'>{t(TEXT.STEP_1)}</Text.H10>
        <Icon className='w-[12.063rem] h-[10.313rem] mt-[2.213rem] mb-[1.739rem]' src={areYouFreelancer} />

        <div className='h-[3.125rem] w-full flex gap-2 justify-center'>
          <LiliButton
            onClick={() => {
              handleHasUserOtherSources(true);
            }}
            className='w-[8.5rem] text-sm'
            id='FullTimeFreelancerYes'
            title={t(BUTTON_TITLES.YES_BTN)}
          />
          <LiliButton
            onClick={() => {
              handleHasUserOtherSources(false);
            }}
            className='w-[8.5rem] text-sm'
            id='FullTimeFreelancerNo'
            title={t(BUTTON_TITLES.NO_BTN)}
          />
        </div>
      </div>
      )}
      {userState.currentStep === STEP_2_OTHER && (
        <div className='flex flex-col items-center w-[350px] mt-[28px]'>
          <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='text-center'>
            {t(HEADER.STEP_2_OTHER_1)}
            <span className='text-action03'>{t(HEADER.STEP_2_OTHER_2)}</span>
            {t(HEADER.STEP_2_OTHER_3)}
          </Text.H8>
          <Text.H10 className='mt-[8px] text-center text-text-main-secondary mb-[40px]'>
            {t(TEXT.STEP_2_OTHER)}
          </Text.H10>
          <InputWithCurrency
            containerClassName=''
            currency={CURRENCY_USD_SIGN}
            currencyClassName='font-metricMedium text-base text-bw07'
            id='OtherIncomeInput'
            inputCLassName={`h-[4rem] focus:border-action03 caret-action03 w-[17.5rem] ${INPUT_MONEY_CSS} ${inputValue === '' ? 'border-bw08' : 'border-action03'}`}
            onChange={handleOnChange}
            value={inputValue}
            inputRef={inputRef as React.Ref<HTMLInputElement>}
            eventKey={ENTER_KEY}
            handleKeyDown={() => {
              if (!isButtonDisabled) handleAnnualSalary(currencyInputValueToNumber(inputValue));
            }}
          />
          <LiliButton
            disabled={isButtonDisabled}
            onClick={() => {
              handleAnnualSalary(currencyInputValueToNumber(inputValue));
            }}
            className='w-[17.5rem] h-[3.125rem] text-sm rounded-umd  mt-[40px]'
            id='setOtherIncomeButton'
            title={t(BUTTON_TITLES.CONTINUE_BTN)}
          />
        </div>
      )}
      {userState.currentStep === STEP_3 && (
        <div className='flex flex-col items-center w-[350px] mt-[28px]'>
          <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='text-center'>
            {t(HEADER.STEP_3_1)}
            <span className='text-action03'>{t(HEADER.STEP_3_GREEN_WORD)}</span>
            {HEADER.STEP_3_2}
          </Text.H8>
          <Text.H10 className='mt-[8px] text-center text-text-main-secondary mb-[40px]'>
            {t(TEXT.STEP_3)}
          </Text.H10>
          <InputWithCurrency
            containerClassName=''
            currency={CURRENCY_USD_SIGN}
            currencyClassName='font-metricMedium text-base text-bw07'
            id='AverageExpensesInput'
            inputCLassName={`h-[4rem] focus:border-action03 caret-action03 w-[17.5rem] ${INPUT_MONEY_CSS} ${inputValue === '' ? 'border-bw08' : 'border-action03'}`}
            onChange={handleOnChange}
            value={inputValue}
            inputRef={inputRef as React.Ref<HTMLInputElement>}
            eventKey={ENTER_KEY}
            handleKeyDown={() => {
              if (!isButtonDisabled) handleAverageExpenses(currencyInputValueToNumber(inputValue));
            }}
          />
          <LiliButton
            disabled={isButtonDisabled}
            onClick={() => {
              handleAverageExpenses(currencyInputValueToNumber(inputValue));
            }}
            className='w-[17.5rem] h-[3.125rem] text-sm rounded-umd  mt-[40px]'
            id='setIncomeButton'
            title={t(BUTTON_TITLES.CONTINUE_BTN)}
          />
        </div>
      )}
      {userState.currentStep === STEP_4 && (
        <div className='flex flex-col items-center w-[350px]'>
          <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='mb-2'>{t(HEADER.STEP_4)}</Text.H8>
          <Text.H10 className='mt-[8px] text-center text-text-main-secondary mb-[40px]'>
            {t(TEXT.STEP_4_1)}
            <span className='font-metricMedium'>{`${Math.round(recommendedPercentage)}%`}</span>
            {t(TEXT.STEP_4_2)}
          </Text.H10>
          <div className='relative w-[17.5rem]'>
            <div className='w-[17.5rem] h-[1.5rem]'>
              <div
                style={{ marginLeft: `${recommendedMargin}px` }}
                className='absolute mt-[25px] flex flex-col items-center z-100'
              >
                <div style={{ zIndex: 100 }} className='w-[2px] h-[10px] bg-action03 relative pointer-events-none' />
                <div className='flex justify-center items-center relative z-100'>
                  <Icon className='w-[0.606rem] h-[0.669rem] mr-[0.125rem]' src={estimatedTaxRateIcon} />
                  <span className='font-metric text-xxs text-action03 leading-[1.563rem]'>{`${getRecommendedPercentage() !== 0 ? (getRecommendedPercentage() * 100).toFixed(0) : DEFAULT_ESTIMATED_PERCENT}%`}</span>
                </div>
              </div>

              <div
                style={{ marginLeft: `${labelMargin}px` }}
                className='absolute w-full mt-[-1rem] -translate-x-[14.08px]'
              >
                <Icon className='w-[2.76rem] h-[2rem]' src={percentageLabel} />
                <span className='absolute w-[2.76rem] text-center mt-[-1.75rem] leading-[1.25rem] text-sm font-metricMedium text-bw00'>
                  {Math.round(percentage).toFixed(0)}
                  %
                </span>
              </div>
            </div>
            <input
              className='absolute w-[17.5rem] h-[0.25rem] bg-action01 rounded-[10px] appearance-none cursor-pointer z-10'
              type='range'
              min={5}
              max={40}
              value={percentage}
              onChange={(event) => {
                setPercentage(parseInt(event.target.value, 10));
              }}
            />
          </div>
          <div className='flex justify-between w-[17.5rem] font-metric text-bw05 text-xxs leading-[1.563rem] mt-[0.375rem] mb-[4.313rem]'>
            <span>{recommendedPercentage !== 5 ? '5%' : ''}</span>
            <span>{recommendedPercentage !== 40 ? '40%' : ''}</span>
          </div>
          <LiliButton
            onClick={() => {
              handleSetSavePercent(percentage / 100);
            }}
            className='w-[17.5rem] h-[3.125rem]'
            id='setYourAutoSave'
            title={t(BUTTON_TITLES.SET_BTN)}
          />
          <span className='text-center leading-[1rem] text-xxs font-metric text-bw06 mt-[0.75rem] px-11'>
            {t(DISCLAIMER)}
          </span>
        </div>
      )}
      {userState.currentStep === STEP_CONFIRMATION && (
        <div className='flex flex-col items-center w-[350px]'>
          <Text.H8 fontWeight={FontWeight.SEMIBOLD} className='mb-2'>{t(HEADER.STEP_CONFIRMATION)}</Text.H8>
          <Text.H10 className='mt-[8px] text-center text-text-main-secondary mb-[40px] w-[17.5rem]'>
            <span className='text-bw08 font-metricMedium'>
              {Math.round(percentage)}
              %
            </span>
            {t(TEXT.STEP_CONFIRMATION)}
          </Text.H10>
          <Donut
            percentageClassName='text-[2.192rem] text-action03 font-metricSemibold'
            percentage={percentage}
            squareSize={130}
            strokeWidth={12}
          />
          <LiliButton
            isLoading={isLoading || isLoadingUploadFile}
            onClick={handleSubmitPercentValue}
            className='w-[17.5rem] h-[3.125rem] mt-[2.875rem] text-sm'
            title={t(BUTTON_TITLES.CONFIRM_BTN)}
            id='confirmTaxBucketPercentage'
          />
        </div>
      )}
    </div>
  );
};

export default TaxBucketPersonalization;
